import { CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import AutoNumeric from 'autonumeric';
import { GalaxyCurrencyInputInterface } from '../..';
import { GalaxyCoreInputDirective } from '../core/core-input.directive';

export type GalaxyCurrencyDecimalSeparator = '.' | ',' | '·' | '⎖' | '٫';
export type GalaxyCurrencyDigitGroupSeparator = ',' | '.' | '٬' | '˙' | "'" | ' ' | '';

/** Deprecated - planned to be replaced by a Galaxy Field version */
@Component({
  selector: 'glxy-currency-input',
  templateUrl: './currency-input.component.html',
  styleUrls: ['./../core/core.material-override.scss', './currency-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true,
    },
    CurrencyPipe,
  ],
})
export class CurrencyInputComponent
  extends GalaxyCoreInputDirective<number>
  implements OnInit, OnChanges, AfterViewInit
{
  @HostBinding('class') class = 'glxy-currency-input';

  /** Additional message to display below the text box */
  @Input() hint?: string;
  /** Currency code to use. E.g. 'USD' or 'CAD' */
  @Input() currencyCode = 'USD';
  /** The separator for the decimal group. E.g. ',' or '.' */
  @Input() decimalSeparator: GalaxyCurrencyDecimalSeparator = '.';
  /** The separator between thousands. E.g. ',' or '.' */
  @Input() digitGroupSeparator: GalaxyCurrencyDigitGroupSeparator = ',';
  /** The amount of decimal places */
  @Input() decimalPlaces = 2;
  /** Max value for the input */
  @Input() maximumValue = '100000';
  /** Minimum value for the input */
  @Input() minimumValue = '-100000';
  /**
   * Locale to display the value as. If a locale is supplied,
   * it will override the decimalSeparator and digitGroupSeparator
   * supplied with the information from the locale
   */
  @Input() locale?: string;
  /** Config for the field */
  @Input() config?: GalaxyCurrencyInputInterface;

  symbol = '';

  @ViewChild('currencyInput') currencyInput?: ElementRef;
  private autoNumericObject?: AutoNumeric;
  internalControl: UntypedFormControl = new UntypedFormControl();

  constructor(private readonly currencyPipe: CurrencyPipe) {
    super();
  }

  ngOnInit(): void {
    this.setupControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (!!changes.locale && !changes.locale.firstChange) ||
      (!!changes.currencyCode && !changes.currencyCode.firstChange) ||
      (!!changes.config && !changes.config.firstChange)
    ) {
      this.getCurrencySymbolFromCode();
    }
  }

  private getCurrencySymbolFromCode(): void {
    // Creates a zero value currency object in a certain locale
    const transformCurrencyCode = this.currencyPipe.transform(
      0,
      this.config?.currencyCode || this.currencyCode,
      undefined,
      undefined,
      'en-US',
    );
    // The value will always look like <symbol>0.00, so we split at the first zero
    //   and set that symbol
    this.symbol = transformCurrencyCode?.split('0')[0] || '';

    // Check if we should auto set the separators
    if (this.locale || this.config?.locale) {
      const transformedCurrency = this.currencyPipe
        .transform(1000, '&', undefined, '1.2-2', this.config?.locale || this.locale)
        ?.replace('&', '')
        .trim()
        .split('');

      // Using the currency pipe above, we break the values into an array. For example, in en-US and USD
      //    ['1', ',', '0', '0', '0', '.', '0', '0']
      // So from the above we can see that:
      //    1: Thousands Group Separator
      //    5: Decimal Group Separator
      if (transformedCurrency) {
        this.digitGroupSeparator = transformedCurrency[1] as GalaxyCurrencyDigitGroupSeparator;
        this.decimalSeparator = transformedCurrency[5] as GalaxyCurrencyDecimalSeparator;
      }

      // If we have the object already created, update it.
      if (this.autoNumericObject) {
        this.autoNumericObject.set(this.internalControl.value || 0, {
          digitGroupSeparator: this.digitGroupSeparator,
          decimalCharacter: this.decimalSeparator,
        });
      }
    }
  }

  ngAfterViewInit(): void {
    this.autoNumericObject = new AutoNumeric(this.currencyInput?.nativeElement, undefined, {
      digitGroupSeparator: this.digitGroupSeparator,
      decimalCharacter: this.decimalSeparator,
      maximumValue: this.maximumValue,
      minimumValue: this.minimumValue,
      decimalPlaces: this.decimalPlaces,
    });
    this.autoNumericObject.set(this.formControl.value || 0);
  }

  setupControl(): void {
    super.setupControl();

    // Creates the internal control for handling the changes
    const formValidators = this.validators.map((validator) => {
      return validator.validatorFn;
    });

    this.internalControl.setValidators(formValidators);
    this.internalControl.setValue(this.formControl.value);
    this.internalControl.updateValueAndValidity();
    this.getCurrencySymbolFromCode();
  }

  /**
   * When the user blurs the input, set the value from the auto-numeric options
   */
  handleBlur(value: any): void {
    const currencyValue = value ? this.autoNumericObject?.getNumber() ?? 0 : 0;
    this.formControl.setValue(currencyValue);
    this.formControl.updateValueAndValidity();
    this.writeValue(currencyValue);
  }

  setDisabledState(isDisabled: boolean): void {
    // Do nothing when no change
    if (isDisabled === this.formControl?.disabled) {
      return;
    }

    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.formControl.disable();
      this.internalControl.disable();
    } else {
      this.formControl.enable();
      this.internalControl.enable();
    }
  }
}
